import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../api";

const CATEGORIES = ["asset", "liability", "equity", "income", "expenditure"];
const CATEGORY_LABEL = { asset: "Assets", liability: "Liabilities", equity: "Equity", income: "Income", expenditure: "Expenditure" };
const VALID_ROLES = ["member", "auditor", "committee", "captain", "assistant_treasurer", "competition_secretary", "membership_secretary", "treasurer", "system_administrator"];
const TABS = ["Overview", "Chart of Accounts", "Banking", "Periods", "Journals", "Roles", "Audit Log"];
const BANK_ACCOUNT_TYPES = ["current", "savings", "petty_cash"];
const BANK_ACCOUNT_TYPE_LABEL = { current: "Current", savings: "Savings", petty_cash: "Petty Cash" };

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Finance() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [tab, setTab] = useState("Overview");
  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };
  const errMsg = (e, fallback) => e.response?.data?.error || fallback;

  const loadSettings = useCallback(() => {
    setLoading(true);
    api.get("/finance/settings")
      .then(res => setSettings(res.data))
      .catch(() => showToast("Failed to load Finance settings"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const enableFinance = async () => {
    setEnabling(true);
    try {
      await api.post("/finance/enable");
      showToast("Finance Module enabled");
      loadSettings();
    } catch (e) {
      showToast(errMsg(e, "Couldn't enable Finance"));
    } finally {
      setEnabling(false);
    }
  };

  if (loading) {
    return <Layout><div className="p-6 text-gray-500">Loading Finance...</div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 max-w-5xl">
        <h1 className="text-2xl font-bold text-green-700 mb-1">💰 Finance</h1>
        <p className="text-sm text-gray-500 mb-6">
          Double-entry ledger, chart of accounts, financial periods, journals and bank reconciliation.
        </p>

        {!settings?.enabled ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-w-xl">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Enable the Finance Module</h2>
            <p className="text-sm text-gray-600 mb-4">
              This seeds a full chart of accounts, opens the current fiscal year with 12 monthly
              periods, and grants you Treasurer and System Administrator access. This cannot be
              undone from here — once enabled, posted journals can only be corrected by reversal,
              never edited or deleted.
            </p>
            <button onClick={enableFinance} disabled={enabling}
              className={`font-semibold py-2 px-5 rounded-lg text-white text-sm ${enabling ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
              {enabling ? "Enabling..." : "Enable Finance Module"}
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
                    tab === t ? "border-green-600 text-green-700" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === "Overview" && <Overview settings={settings} showToast={showToast} errMsg={errMsg} />}
            {tab === "Chart of Accounts" && <ChartOfAccounts showToast={showToast} errMsg={errMsg} />}
            {tab === "Banking" && <Banking showToast={showToast} errMsg={errMsg} />}
            {tab === "Periods" && <Periods showToast={showToast} errMsg={errMsg} />}
            {tab === "Journals" && <Journals showToast={showToast} errMsg={errMsg} />}
            {tab === "Roles" && <Roles showToast={showToast} errMsg={errMsg} />}
            {tab === "Audit Log" && <AuditLog />}
          </>
        )}

        {toast && <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm">{toast}</div>}
      </div>
    </Layout>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
      {title && <h2 className="text-lg font-semibold text-gray-800 mb-3">{title}</h2>}
      {children}
    </div>
  );
}

// ---------- Overview ----------

function Overview({ settings, showToast, errMsg }) {
  const [periods, setPeriods] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/finance/periods"), api.get("/finance/fiscal-years")]).then(([p, fy]) => {
      setPeriods(p.data);
      setFiscalYears(fy.data);
      const today = new Date();
      const current = p.data.find(pd => new Date(pd.starts_on) <= today && today <= new Date(pd.ends_on));
      setCurrentPeriod(current || p.data[p.data.length - 1] || null);
    }).catch(() => showToast("Failed to load overview"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentPeriod) return;
    api.get(`/finance/trial-balance?period_id=${currentPeriod.id}`)
      .then(res => setTrialBalance(res.data))
      .catch(() => {});
  }, [currentPeriod]);

  const overduePeriods = periods.filter(p => !p.is_locked && new Date(p.ends_on) < new Date());

  return (
    <>
      {overduePeriods.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-xl p-4 mb-6 text-sm">
          ⚠ {overduePeriods.length} period{overduePeriods.length > 1 ? "s have" : " has"} ended but
          {overduePeriods.length > 1 ? " aren't" : " isn't"} locked yet — see the Periods tab.
        </div>
      )}

      <Card title="Settings">
        <div className="text-sm text-gray-700 space-y-1">
          <div>Accounting basis: <span className="font-medium capitalize">{settings.accounting_basis}</span></div>
          <div>Fiscal year starts: <span className="font-medium">month {settings.fiscal_year_start_month}</span></div>
          <div>Enabled: <span className="font-medium">{fmtDate(settings.enabled_at)}</span></div>
          <div>Fiscal years on record: <span className="font-medium">{fiscalYears.length}</span></div>
        </div>
      </Card>

      <Card title={currentPeriod ? `Trial Balance — ${fmtDate(currentPeriod.starts_on)} to ${fmtDate(currentPeriod.ends_on)}` : "Trial Balance"}>
        {!trialBalance ? (
          <p className="text-sm text-gray-500">No period to show yet.</p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1">Code</th><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {trialBalance.accounts.filter(a => Number(a.total_debit) !== 0 || Number(a.total_credit) !== 0).map(a => (
                  <tr key={a.account_id} className="border-b border-gray-100">
                    <td className="py-1 text-gray-500">{a.code}</td>
                    <td>{a.name}</td>
                    <td className="text-right font-mono">{money(a.total_debit)}</td>
                    <td className="text-right font-mono">{money(a.total_credit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold border-t">
                  <td className="py-1" colSpan={2}>Total</td>
                  <td className="text-right font-mono">{money(trialBalance.totals.debit)}</td>
                  <td className="text-right font-mono">{money(trialBalance.totals.credit)}</td>
                </tr>
              </tfoot>
            </table>
            {trialBalance.accounts.every(a => Number(a.total_debit) === 0 && Number(a.total_credit) === 0) && (
              <p className="text-sm text-gray-500 mt-2">No journals posted into this period yet.</p>
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ---------- Banking ----------

function Banking({ showToast, errMsg }) {
  const [banks, setBanks] = useState([]);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [showAddBank, setShowAddBank] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newBank, setNewBank] = useState({ name: "", account_type: "current", linked_account_id: "", opening_balance: "0", opening_balance_date: new Date().toISOString().slice(0, 10) });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/finance/bank-accounts"), api.get("/finance/accounts")])
      .then(([b, a]) => { setBanks(b.data); setLedgerAccounts(a.data.filter(x => x.is_active)); })
      .catch(() => showToast("Failed to load bank accounts"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const addBank = async () => {
    if (!newBank.name || !newBank.linked_account_id || !newBank.opening_balance_date) {
      showToast("Name, linked ledger account and opening balance date are required"); return;
    }
    setAdding(true);
    try {
      await api.post("/finance/bank-accounts", { ...newBank, linked_account_id: Number(newBank.linked_account_id), opening_balance: parseFloat(newBank.opening_balance) || 0 });
      showToast("Bank account added");
      setShowAddBank(false);
      setNewBank({ name: "", account_type: "current", linked_account_id: "", opening_balance: "0", opening_balance_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't add bank account")); } finally { setAdding(false); }
  };

  const assetAccounts = ledgerAccounts.filter(a => a.category === "asset");
  const selectedBank = banks.find(b => b.id === selectedBankId);

  if (loading) return <p className="text-sm text-gray-500">Loading bank accounts...</p>;

  return (
    <>
      <Card title="Bank Accounts">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-1">Name</th><th>Type</th><th className="text-right">Balance</th><th className="text-right">Unreconciled</th><th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {banks.map(b => (
              <tr key={b.id} className="border-b border-gray-100">
                <td className="py-1">{b.name}</td>
                <td>{BANK_ACCOUNT_TYPE_LABEL[b.account_type]}</td>
                <td className="text-right font-mono">{money(b.running_balance)}</td>
                <td className="text-right">
                  {Number(b.unreconciled_count) > 0
                    ? <span className="inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">{b.unreconciled_count}</span>
                    : <span className="text-gray-400">0</span>}
                </td>
                <td>
                  <button onClick={() => setSelectedBankId(b.id)}
                    className="text-xs font-medium px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-50">
                    Open
                  </button>
                </td>
              </tr>
            ))}
            {!banks.length && <tr><td colSpan={5} className="py-3 text-gray-500">No bank accounts yet.</td></tr>}
          </tbody>
        </table>

        <div className="mt-4">
          <button onClick={() => setShowAddBank(s => !s)} className="font-semibold py-2 px-5 rounded-lg text-white text-sm bg-green-600 hover:bg-green-700">
            {showAddBank ? "Cancel" : "+ Add Bank Account"}
          </button>
        </div>

        {showAddBank && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input value={newBank.name} onChange={e => setNewBank(n => ({ ...n, name: e.target.value }))}
                className="w-48 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select value={newBank.account_type} onChange={e => setNewBank(n => ({ ...n, account_type: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                {BANK_ACCOUNT_TYPES.map(t => <option key={t} value={t}>{BANK_ACCOUNT_TYPE_LABEL[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Linked Ledger Account</label>
              <select value={newBank.linked_account_id} onChange={e => setNewBank(n => ({ ...n, linked_account_id: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-56">
                <option value="">Select asset account...</option>
                {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Opening Balance</label>
              <input type="number" step="0.01" value={newBank.opening_balance} onChange={e => setNewBank(n => ({ ...n, opening_balance: e.target.value }))}
                className="w-28 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">As of</label>
              <input type="date" value={newBank.opening_balance_date} onChange={e => setNewBank(n => ({ ...n, opening_balance_date: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
            </div>
            <button onClick={addBank} disabled={adding}
              className={`font-semibold py-1.5 px-4 rounded-lg text-white text-sm ${adding ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
              {adding ? "Adding..." : "Add"}
            </button>
          </div>
        )}
      </Card>

      {selectedBank && (
        <BankAccountDetail bank={selectedBank} ledgerAccounts={ledgerAccounts} showToast={showToast} errMsg={errMsg}
          onClose={() => setSelectedBankId(null)} onChanged={load} />
      )}
    </>
  );
}

function BankAccountDetail({ bank, ledgerAccounts, showToast, errMsg, onClose, onChanged }) {
  const [transactions, setTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [categorizingId, setCategorizingId] = useState(null);
  const [categoryChoice, setCategoryChoice] = useState({});
  const [reconForm, setReconForm] = useState({ statement_date: new Date().toISOString().slice(0, 10), statement_balance: "" });
  const [reconciling, setReconciling] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/finance/bank-accounts/${bank.id}/transactions?status=unreconciled`),
      api.get(`/finance/bank-accounts/${bank.id}/reconciliations`),
    ]).then(([t, r]) => { setTransactions(t.data); setReconciliations(r.data); })
      .catch(() => showToast("Failed to load bank account detail"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [bank.id]);

  const importCsv = async () => {
    if (!csvText.trim()) { showToast("Paste some CSV text first"); return; }
    setImporting(true);
    try {
      const res = await api.post(`/finance/bank-accounts/${bank.id}/import`, { csv: csvText });
      showToast(`Imported ${res.data.imported}, skipped ${res.data.skipped_duplicates} duplicates`);
      setCsvText("");
      load();
      onChanged();
    } catch (e) { showToast(errMsg(e, "Couldn't import CSV")); } finally { setImporting(false); }
  };

  const categorize = async (txn) => {
    const accountId = categoryChoice[txn.id];
    if (!accountId) { showToast("Choose a category first"); return; }
    setCategorizingId(txn.id);
    try {
      await api.post(`/finance/bank-transactions/${txn.id}/categorize`, { account_id: Number(accountId) });
      showToast("Transaction categorized");
      load();
      onChanged();
    } catch (e) { showToast(errMsg(e, "Couldn't categorize transaction")); } finally { setCategorizingId(null); }
  };

  const completeReconciliation = async () => {
    if (!reconForm.statement_date || reconForm.statement_balance === "") { showToast("Statement date and balance are required"); return; }
    setReconciling(true);
    try {
      const res = await api.post(`/finance/bank-accounts/${bank.id}/reconciliations`, { ...reconForm, statement_balance: parseFloat(reconForm.statement_balance) });
      showToast(res.data.is_balanced ? "Reconciliation PASSED" : "Reconciliation does not balance — saved for review");
      setReconForm({ statement_date: new Date().toISOString().slice(0, 10), statement_balance: "" });
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't complete reconciliation")); } finally { setReconciling(false); }
  };

  return (
    <Card title={`${bank.name} — Banking Detail`}>
      <button onClick={onClose} className="text-sm text-gray-500 hover:underline mb-4">← Back to all bank accounts</button>

      <h3 className="text-sm font-semibold text-gray-700 mb-2">Import Statement (CSV)</h3>
      <p className="text-xs text-gray-500 mb-2">Paste CSV with columns date, description, amount (a header row is detected automatically). Money in is positive, money out is negative.</p>
      <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={4}
        placeholder={"date,description,amount\n2026-10-05,Membership fee received,150.00\n2026-10-06,Bank charges,-5.00"}
        className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm font-mono mb-2" />
      <button onClick={importCsv} disabled={importing}
        className={`font-semibold py-1.5 px-4 rounded-lg text-white text-sm ${importing ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
        {importing ? "Importing..." : "Import"}
      </button>

      <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">Unreconciled Transactions</h3>
      {loading ? <p className="text-sm text-gray-500">Loading...</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-1">Date</th><th>Description</th><th className="text-right">Amount</th><th className="w-48">Category</th><th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(t => (
              <tr key={t.id} className="border-b border-gray-100">
                <td className="py-1">{fmtDate(t.txn_date)}</td>
                <td>{t.description}</td>
                <td className="text-right font-mono">{money(t.amount)}</td>
                <td>
                  <select value={categoryChoice[t.id] || ""} onChange={e => setCategoryChoice(c => ({ ...c, [t.id]: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full">
                    <option value="">Select account...</option>
                    {ledgerAccounts.map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                  </select>
                </td>
                <td>
                  <button onClick={() => categorize(t)} disabled={categorizingId === t.id}
                    className={`text-xs font-semibold px-3 py-1 rounded-lg text-white ${categorizingId === t.id ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
                    {categorizingId === t.id ? "Categorizing..." : "Categorize"}
                  </button>
                </td>
              </tr>
            ))}
            {!transactions.length && <tr><td colSpan={5} className="py-3 text-gray-500">Nothing to categorize — all caught up.</td></tr>}
          </tbody>
        </table>
      )}

      <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">Complete a Reconciliation</h3>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Statement Date</label>
          <input type="date" value={reconForm.statement_date} onChange={e => setReconForm(f => ({ ...f, statement_date: e.target.value }))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Statement Balance</label>
          <input type="number" step="0.01" value={reconForm.statement_balance} onChange={e => setReconForm(f => ({ ...f, statement_balance: e.target.value }))}
            className="w-32 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right" />
        </div>
        <button onClick={completeReconciliation} disabled={reconciling}
          className={`font-semibold py-1.5 px-4 rounded-lg text-white text-sm ${reconciling ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
          {reconciling ? "Checking..." : "Complete Reconciliation"}
        </button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-1">Statement Date</th><th className="text-right">Statement Balance</th><th className="text-right">Calculated Balance</th><th>Result</th><th>Completed By</th>
          </tr>
        </thead>
        <tbody>
          {reconciliations.map(r => (
            <tr key={r.id} className="border-b border-gray-100">
              <td className="py-1">{fmtDate(r.statement_date)}</td>
              <td className="text-right font-mono">{money(r.statement_balance)}</td>
              <td className="text-right font-mono">{money(r.calculated_balance)}</td>
              <td>
                {r.is_balanced
                  ? <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">PASS</span>
                  : <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">FAIL</span>}
              </td>
              <td className="text-gray-500">{r.completed_by}</td>
            </tr>
          ))}
          {!reconciliations.length && <tr><td colSpan={5} className="py-3 text-gray-500">No reconciliations completed yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

// ---------- Chart of Accounts ----------

function ChartOfAccounts({ showToast, errMsg }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAcc, setNewAcc] = useState({ code: "", name: "", category: "asset" });
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/finance/accounts").then(res => setAccounts(res.data)).catch(() => showToast("Failed to load accounts")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActive = async (acc) => {
    try {
      const res = await api.put(`/finance/accounts/${acc.id}`, { is_active: !acc.is_active });
      setAccounts(accs => accs.map(a => a.id === acc.id ? res.data : a));
    } catch (e) { showToast(errMsg(e, "Couldn't update account")); }
  };

  const rename = async (acc, name) => {
    if (!name || name === acc.name) return;
    try {
      const res = await api.put(`/finance/accounts/${acc.id}`, { name });
      setAccounts(accs => accs.map(a => a.id === acc.id ? res.data : a));
    } catch (e) { showToast(errMsg(e, "Couldn't rename account")); }
  };

  const addAccount = async () => {
    if (!newAcc.code || !newAcc.name) { showToast("Code and name are required"); return; }
    setAdding(true);
    try {
      const res = await api.post("/finance/accounts", newAcc);
      setAccounts(accs => [...accs, res.data].sort((a, b) => a.code.localeCompare(b.code)));
      setNewAcc({ code: "", name: "", category: "asset" });
      showToast("Account added");
    } catch (e) { showToast(errMsg(e, "Couldn't add account")); } finally { setAdding(false); }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading accounts...</p>;

  return (
    <>
      {CATEGORIES.map(cat => {
        const group = accounts.filter(a => a.category === cat);
        if (!group.length) return null;
        return (
          <Card key={cat} title={CATEGORY_LABEL[cat]}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 w-20">Code</th><th>Name</th><th className="w-24">Active</th>
                </tr>
              </thead>
              <tbody>
                {group.map(a => (
                  <tr key={a.id} className="border-b border-gray-100">
                    <td className="py-1 text-gray-500">{a.code}</td>
                    <td>
                      <input defaultValue={a.name} onBlur={e => rename(a, e.target.value)}
                        className="border border-transparent hover:border-gray-300 focus:border-green-500 rounded px-1 py-0.5 text-sm w-full" />
                    </td>
                    <td>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={a.is_active} onChange={() => toggleActive(a)} className="w-4 h-4 accent-green-600" />
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        );
      })}

      <Card title="Add Account">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Code</label>
            <input value={newAcc.code} onChange={e => setNewAcc(n => ({ ...n, code: e.target.value }))}
              className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input value={newAcc.name} onChange={e => setNewAcc(n => ({ ...n, name: e.target.value }))}
              className="w-56 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select value={newAcc.category} onChange={e => setNewAcc(n => ({ ...n, category: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
              {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </select>
          </div>
          <button onClick={addAccount} disabled={adding}
            className={`font-semibold py-1.5 px-4 rounded-lg text-white text-sm ${adding ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
            {adding ? "Adding..." : "Add"}
          </button>
        </div>
      </Card>
    </>
  );
}

// ---------- Periods ----------

function Periods({ showToast, errMsg }) {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingFY, setCreatingFY] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/finance/fiscal-years"), api.get("/finance/periods")])
      .then(([fy, p]) => { setFiscalYears(fy.data); setPeriods(p.data); })
      .catch(() => showToast("Failed to load periods"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleLock = async (period) => {
    try {
      const res = await api.post(`/finance/periods/${period.id}/${period.is_locked ? "unlock" : "lock"}`);
      setPeriods(ps => ps.map(p => p.id === period.id ? res.data : p));
    } catch (e) { showToast(errMsg(e, "Couldn't update period")); }
  };

  const createNextFiscalYear = async () => {
    setCreatingFY(true);
    try {
      const latest = fiscalYears[fiscalYears.length - 1];
      await api.post("/finance/fiscal-years", latest ? { after_fiscal_year_id: latest.id } : {});
      showToast("New fiscal year created");
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't create fiscal year")); } finally { setCreatingFY(false); }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading periods...</p>;

  return (
    <>
      {fiscalYears.map(fy => (
        <Card key={fy.id} title={`Fiscal Year: ${fmtDate(fy.starts_on)} – ${fmtDate(fy.ends_on)}${fy.is_closed ? " (closed)" : ""}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1">Period</th><th>Status</th><th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {periods.filter(p => p.fiscal_year_id === fy.id).map(p => (
                <tr key={p.id} className="border-b border-gray-100">
                  <td className="py-1">{fmtDate(p.starts_on)} – {fmtDate(p.ends_on)}</td>
                  <td>{p.is_locked ? <span className="text-red-600">🔒 Locked</span> : <span className="text-green-600">Open</span>}</td>
                  <td>
                    <button onClick={() => toggleLock(p)}
                      className="text-xs font-medium px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50">
                      {p.is_locked ? "Unlock" : "Lock"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      <button onClick={createNextFiscalYear} disabled={creatingFY}
        className={`font-semibold py-2 px-5 rounded-lg text-white text-sm ${creatingFY ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
        {creatingFY ? "Creating..." : "+ Start Next Fiscal Year"}
      </button>
    </>
  );
}

// ---------- Journals ----------

function emptyLine() { return { account_id: "", debit: "", credit: "", description: "" }; }

function Journals({ showToast, errMsg }) {
  const [journals, setJournals] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [form, setForm] = useState({ period_id: "", entry_date: new Date().toISOString().slice(0, 10), description: "", reference: "", lines: [emptyLine(), emptyLine()] });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/finance/journals"), api.get("/finance/periods"), api.get("/finance/accounts")])
      .then(([j, p, a]) => { setJournals(j.data); setPeriods(p.data); setAccounts(a.data.filter(x => x.is_active)); })
      .catch(() => showToast("Failed to load journals"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setLine = (i, field, value) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [field]: value } : l) }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  const postJournal = async () => {
    if (!form.period_id || !form.description) { showToast("Period and description are required"); return; }
    if (!balanced) { showToast("Journal does not balance"); return; }
    setPosting(true);
    try {
      await api.post("/finance/journals", {
        ...form,
        lines: form.lines.filter(l => l.account_id).map(l => ({
          account_id: Number(l.account_id), debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, description: l.description || undefined,
        })),
      });
      showToast("Journal posted");
      setShowForm(false);
      setForm({ period_id: form.period_id, entry_date: new Date().toISOString().slice(0, 10), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't post journal")); } finally { setPosting(false); }
  };

  const reverseJournal = async (journal) => {
    try {
      await api.post(`/finance/journals/${journal.id}/reverse`);
      showToast(`Journal #${journal.id} reversed`);
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't reverse journal")); }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading journals...</p>;

  return (
    <>
      <div className="mb-4">
        <button onClick={() => setShowForm(s => !s)}
          className="font-semibold py-2 px-5 rounded-lg text-white text-sm bg-green-600 hover:bg-green-700">
          {showForm ? "Cancel" : "+ New Journal"}
        </button>
      </div>

      {showForm && (
        <Card title="New Journal">
          <div className="flex flex-wrap gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Period</label>
              <select value={form.period_id} onChange={e => setForm(f => ({ ...f, period_id: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-56">
                <option value="">Select period...</option>
                {periods.filter(p => !p.is_locked).map(p => (
                  <option key={p.id} value={p.id}>{fmtDate(p.starts_on)} – {fmtDate(p.ends_on)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date</label>
              <input type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
            </div>
            <div className="flex-1 min-w-48">
              <label className="block text-xs text-gray-500 mb-1">Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reference</label>
              <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-32" />
            </div>
          </div>

          <table className="w-full text-sm mb-2">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1">Account</th><th className="w-28 text-right">Debit</th><th className="w-28 text-right">Credit</th><th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1">
                    <select value={l.account_id} onChange={e => setLine(i, "account_id", e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full">
                      <option value="">Select account...</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                    </select>
                  </td>
                  <td><input type="number" step="0.01" value={l.debit} onChange={e => setLine(i, "debit", e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full text-right" /></td>
                  <td><input type="number" step="0.01" value={l.credit} onChange={e => setLine(i, "credit", e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full text-right" /></td>
                  <td>
                    {form.lines.length > 2 && (
                      <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-600 text-sm">✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="py-1">Total</td>
                <td className="text-right font-mono">{money(totalDebit)}</td>
                <td className="text-right font-mono">{money(totalCredit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          <div className="flex items-center justify-between">
            <button onClick={addLine} className="text-sm text-green-700 hover:underline">+ Add line</button>
            <div className="flex items-center gap-3">
              {!balanced && <span className="text-sm text-amber-700">Debits and credits must match and be non-zero</span>}
              <button onClick={postJournal} disabled={posting || !balanced}
                className={`font-semibold py-2 px-5 rounded-lg text-white text-sm ${posting || !balanced ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
                {posting ? "Posting..." : "Post Journal"}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card title="Journals">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-1">#</th><th>Date</th><th>Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th>Status</th><th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {journals.map(j => (
              <tr key={j.id} className="border-b border-gray-100">
                <td className="py-1 text-gray-500">{j.id}</td>
                <td>{fmtDate(j.entry_date)}</td>
                <td>{j.description}</td>
                <td className="text-right font-mono">{money(j.total_debit)}</td>
                <td className="text-right font-mono">{money(j.total_credit)}</td>
                <td>{j.status === "reversed" ? <span className="text-gray-400">Reversed</span> : <span className="text-green-700">Posted</span>}</td>
                <td>
                  {j.status === "posted" && (
                    <button onClick={() => reverseJournal(j)} className="text-xs font-medium px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-50">
                      Reverse
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!journals.length && <tr><td colSpan={7} className="py-3 text-gray-500">No journals posted yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ---------- Roles ----------

function Roles({ showToast, errMsg }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ identity_label: "", role: "member" });
  const [granting, setGranting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/finance/roles").then(res => setRoles(res.data)).catch(() => showToast("Failed to load roles")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grant = async () => {
    if (!form.identity_label) { showToast("An email/identity is required"); return; }
    setGranting(true);
    try {
      await api.post("/finance/roles", form);
      showToast("Role granted");
      setForm({ identity_label: "", role: "member" });
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't grant role")); } finally { setGranting(false); }
  };

  const revoke = async (role) => {
    try {
      await api.delete(`/finance/roles/${role.id}`);
      showToast("Role revoked");
      load();
    } catch (e) { showToast(errMsg(e, "Couldn't revoke role")); }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading roles...</p>;

  return (
    <>
      <Card title="Current Role Assignments">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-1">Identity</th><th>Role</th><th>Granted</th><th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.id} className="border-b border-gray-100">
                <td className="py-1">{r.identity_label}</td>
                <td className="capitalize">{r.role.replace(/_/g, " ")}</td>
                <td className="text-gray-500">{fmtDate(r.granted_at)}</td>
                <td><button onClick={() => revoke(r)} className="text-xs font-medium px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-50">Revoke</button></td>
              </tr>
            ))}
            {!roles.length && <tr><td colSpan={4} className="py-3 text-gray-500">No role assignments yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="Grant a Role">
        <p className="text-xs text-gray-500 mb-3">
          Use the email of the primary account or an existing co-organiser. View: Member, Auditor, Committee, Captain.
          Post: Assistant Treasurer, Competition Secretary, Membership Secretary. Administer: Treasurer, System Administrator.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Identity (email)</label>
            <input value={form.identity_label} onChange={e => setForm(f => ({ ...f, identity_label: e.target.value }))}
              className="w-64 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Role</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
              {VALID_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <button onClick={grant} disabled={granting}
            className={`font-semibold py-1.5 px-4 rounded-lg text-white text-sm ${granting ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
            {granting ? "Granting..." : "Grant"}
          </button>
        </div>
      </Card>
    </>
  );
}

// ---------- Audit Log ----------

function AuditLog() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/finance/audit-log").then(res => setLog(res.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = actionFilter ? log.filter(l => l.action === actionFilter) : log;
  const actions = Array.from(new Set(log.map(l => l.action))).sort();

  if (loading) return <p className="text-sm text-gray-500">Loading audit log...</p>;

  return (
    <Card title="Audit Log">
      <div className="mb-3">
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
          <option value="">All actions</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-1">When</th><th>Actor</th><th>Action</th><th>Target</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(l => (
            <tr key={l.id} className="border-b border-gray-100">
              <td className="py-1 text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-GB")}</td>
              <td>{l.actor_identity}</td>
              <td>{l.action}</td>
              <td className="text-gray-500">{l.target_type ? `${l.target_type} #${l.target_id}` : ""}</td>
            </tr>
          ))}
          {!filtered.length && <tr><td colSpan={4} className="py-3 text-gray-500">No activity yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
